import { Grid, IconButton, Tab, Tabs } from "@mui/material";
import {
  getCountyOptionsByCountry,
  leadCollectionAllFields,
} from "./leadAllFields";
import FormField from "../../../components/common/FormField";
import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  addCollectionAddress,
  deleteLeadAddressAction,
  getCalculateDistanceAction,
  removeCollectionAddress,
  setCalculateCollectionDistanceAction,
  setNewLeadLocal,
} from "../../../store/reducers/leadSlice";
import CustomButton from "../../../components/common/button/CustomButton";
import { RemoveCircleRounded } from "@mui/icons-material";

const DEFAULT_PROPERTY_TYPES = [
  "Unknown",
  "House",
  "Town House",
  "Mainsonette",
  "Flat",
  "Bungalow",
  "Cottage",
  "Self Store",
  "Commercial",
];

const DEFAULT_POSTCODE_TRIGGER_LENGTH = 6;

const POSTCODE_TRIGGER_LENGTH_BY_COUNTRY: Record<string, number> = {
  England: 5,
  Wales: 5,
  Scotland: 5,
  "Northern Ireland": 5,
  India: 6,
  Ireland: 7,
  Spain: 5,
  Denmark: 4,
  France: 5,
  Netherlands: 6,
  Portugal: 7,
  USA: 5,
  "New Zealand": 4,
  Australia: 4,
  Australiya: 4,
  Canada: 6,
  Dubai: 3,
};

const getPostcodeTriggerLength = (country?: string) => {
  if (!country) return DEFAULT_POSTCODE_TRIGGER_LENGTH;
  return (
    POSTCODE_TRIGGER_LENGTH_BY_COUNTRY[country] ||
    DEFAULT_POSTCODE_TRIGGER_LENGTH
  );
};

interface ManageCollectionFormProps {
  checkValidator?: any;
  resetValidator?: any;
  error?: any;
  formData?: any;
  setFormData?: any;
}

const CollectionForm: FC<ManageCollectionFormProps> = ({
  checkValidator,
  resetValidator,
  error,
  formData,
  setFormData,
}) => {
  const dispatch = useDispatch();
  const { newLeadLocal, distanceCollectionValue } = useSelector(
    (state: any) => state.lead,
  );
  const { propertyTypeList } = useSelector((state: any) => state.leadSurvey);

  const { userProfile } = useSelector((state: any) => state.user);
  const [isBasicDiarySection, setIsBasicDiarySection] = useState(
    userProfile?.features?.includes("Basic Work Diary") || false, // Safe fallback
  );

  useEffect(() => {
    setIsBasicDiarySection(
      userProfile?.features?.includes("Basic Work Diary") || false,
    ); // Sync profile changes
  }, [userProfile]);

  // State to track manual changes to the postcode field
  const [isPostcodeManuallyChanged, setIsPostcodeManuallyChanged] =
    useState(false);

  // Handle unit change and convert volume if necessary
  const handleUnitChange = (unit: string, volumeValue?: number) => {
    const currentAddress = newLeadLocal?.collectionAddress?.[activeTab] || {};

    const currentVolume = volumeValue ?? Number(currentAddress?.volume) ?? 0;

    const currentUnit = currentAddress?.volumeUnit || "m3";

    let newVolume = currentVolume;

    if (unit === "ft3" && currentUnit === "m3") {
      newVolume = Number((currentVolume * 35.3147).toFixed(2));
    }

    if (unit === "m3" && currentUnit === "ft3") {
      newVolume = Number((currentVolume / 35.3147).toFixed(2));
    }

    const updatedCollectionAddress = [
      ...(newLeadLocal.collectionAddress || []),
    ];

    updatedCollectionAddress[activeTab] = {
      ...currentAddress,
      volume: newVolume,
      volumeUnit: unit,
    };

    dispatch(
      setNewLeadLocal({
        ...newLeadLocal,
        collectionAddress: updatedCollectionAddress,
      }),
    );

    setFormData((prev: any) => {
      const updated = [...(prev.collectionAddress || [])];

      updated[activeTab] = {
        ...updated[activeTab],
        volume: newVolume,
        volumeUnit: unit,
      };

      return {
        ...prev,
        collectionAddress: updated,
      };
    });
  };

  const isCollectionField = (fieldId: string) =>
    [
      "building_number",
      "street",
      "town",
      "county",
      "postcode",
      "country",
      "purchaseStatus",
      "houseSize",
      "distance",
      "volume",
      "volumeUnit",
      "type",
    ].includes(fieldId);

  // Handle input changes
  const onChangeAction = (event: any) => {
    const { id, value } = event.target;
    const updatedValue =
      id === "postcode" ? value?.toUpperCase() || value : value;

    const isCollectionField = [
      "building_number",
      "street",
      "town",
      "county",
      "postcode",
      "country",
      "purchaseStatus",
      "houseSize",
      "distance",
      "volume",
      "volumeUnit",
      "type",
    ].includes(id);
    let updatedCollectionAddress = [...(newLeadLocal.collectionAddress || [])];

    if (isCollectionField && updatedCollectionAddress[activeTab]) {
      updatedCollectionAddress[activeTab] = {
        ...updatedCollectionAddress[activeTab],
        [id]: updatedValue,
      };
    }

    const updatedNewLeadLocal = {
      ...newLeadLocal,
      ...(isCollectionField
        ? { collectionAddress: updatedCollectionAddress }
        : {
            [id]: updatedValue,
          }),
    };

    dispatch(setNewLeadLocal(updatedNewLeadLocal));

    // ✅ Update formData for the specific active delivery address
    setFormData((prevState: any) => {
      const newState = { ...prevState };
      if (isCollectionField) {
        const updatedAddresses = [...(prevState.collectionAddress || [])];
        updatedAddresses[activeTab] = {
          ...updatedAddresses[activeTab],
          [id]: updatedValue,
        };
        newState.collectionAddress = updatedAddresses;
      } else {
        newState[id] = updatedValue;
      }
      return newState;
    });

    // Trigger distance check on postcode typing, and also on country change
    // when a valid postcode is already present for that country rule.
    if (id === "postcode") {
      setIsPostcodeManuallyChanged(true);
    } else if (id === "country") {
      const postcodeForSelectedCountry =
        updatedCollectionAddress?.[activeTab]?.postcode || "";
      const normalizedPostcode = postcodeForSelectedCountry
        .toString()
        .replace(/\s|-/g, "");
      const requiredLength = getPostcodeTriggerLength(updatedValue);

      setIsPostcodeManuallyChanged(
        normalizedPostcode.length === requiredLength,
      );
    } else {
      setIsPostcodeManuallyChanged(false);
    }

    if (id === "collectionVolumeUnit" || id === "volumeUnit") {
      // Handle unit change
      handleUnitChange(updatedValue);
    } else {
      // Volume should keep the raw typed value so intermediate decimals like
      // `2.` are not collapsed to `2` during typing.
      if (id !== "collectionVolume" && id !== "volume") {
        setFormData({
          ...formData,
          [id]: updatedValue,
        });
      }
    }

    resetValidator(id);
  };

  // Set county dropdown options based on country
  const getCountyOptions = (country: string) => {
    return getCountyOptionsByCountry(country);
  };

  const getPropertyTypeOptions = (index: number) => {
    const configuredPropertyTypes = Array.isArray(propertyTypeList)
      ? propertyTypeList
          .map((item: any) => item?.name)
          .filter((name: any) => typeof name === "string" && name.trim() !== "")
      : [];

    const baseOptions =
      configuredPropertyTypes.length > 0
        ? configuredPropertyTypes
        : DEFAULT_PROPERTY_TYPES;

    const savedTypeName = newLeadLocal?.collectionAddress?.[index]?.type;
    const hasSavedTypeInOptions =
      typeof savedTypeName === "string" &&
      baseOptions.some(
        (option: string) => option.toLowerCase() === savedTypeName.toLowerCase(),
      );

    if (
      typeof savedTypeName === "string" &&
      savedTypeName.trim() !== "" &&
      !hasSavedTypeInOptions
    ) {
      return [...baseOptions, savedTypeName];
    }

    return baseOptions;
  };

  // Handle blur action
  const onBlurAction = (e: any) => {
    checkValidator(e.target.id);
  };

  // Effect to check default unit value on mount
  useEffect(() => {
    handleUnitChange(
      formData?.collectionAddress?.[activeTab]?.volumeUnit ||
        newLeadLocal?.collectionAddress?.[activeTab]?.volumeUnit ||
        "m3",
    );
  }, []);

  // State to manage the active tab
  const [activeTab, setActiveTab] = useState(0);
  const activeCollectionAddress =
    newLeadLocal.collectionAddress?.[activeTab] || {};
  const activePostcode = activeCollectionAddress?.postcode;
  const activeCountry = activeCollectionAddress?.country;

  // Effect to call distance API
  useEffect(() => {
    const currentAddress = newLeadLocal.collectionAddress?.[activeTab] || {};
    const currentPostcode = currentAddress?.postcode || "";
    const currentCountry = activeCountry;
    const normalizedCurrentPostcode = currentPostcode
      .toString()
      .replace(/\s|-/g, "");
    const currentRequiredLength = getPostcodeTriggerLength(currentCountry);

    if (
      isPostcodeManuallyChanged &&
      normalizedCurrentPostcode.length >= currentRequiredLength
    ) {
      const prevTabIndex = activeTab - 1;
      const previousAddress =
        prevTabIndex >= 0 ? newLeadLocal.collectionAddress?.[prevTabIndex] : null;
      const previousPostcodeRaw = previousAddress?.postcode || "";
      const previousCountry = previousAddress?.country;
      const normalizedPreviousPostcode = previousPostcodeRaw
        .toString()
        .replace(/\s|-/g, "");
      const previousRequiredLength = getPostcodeTriggerLength(previousCountry);

      const previousPostcode =
        prevTabIndex >= 0 &&
        normalizedPreviousPostcode.length >= previousRequiredLength
          ? previousAddress?.postcode
          : userProfile?.transportCode;

      const postcodeData = {
        postcode1: previousPostcode,
        country1: previousCountry,
        postcode2: currentAddress?.postcode,
        country2: currentAddress?.country,
        type: "collection",
      };

      dispatch(getCalculateDistanceAction(postcodeData));
      setIsPostcodeManuallyChanged(false);
    }
  }, [
    activePostcode,
    activeCountry,
    newLeadLocal.collectionAddress,
    userProfile?.transportCode,
    dispatch,
    isPostcodeManuallyChanged,
    activeTab,
  ]);

  // Effect to update formData with distance value when it changes
  useEffect(() => {
    if (distanceCollectionValue) {
      const prevTabIndex = activeTab - 1;

      // Get previous distance if available
      const prevDistance =
        prevTabIndex >= 0
          ? Number(
              newLeadLocal.collectionAddress?.[prevTabIndex]?.distance || 0,
            )
          : 0;

      // const totalDistance = Number(distanceCollectionValue) + prevDistance;
      const totalDistance = Number(distanceCollectionValue);
      setFormData((prevState: any) => ({
        ...prevState,
        collectionAddress: prevState.collectionAddress.map(
          (item: any, index: number) =>
            index === activeTab
              ? { ...item, distance: totalDistance ?? 0 }
              : item,
        ),
      }));
      // Update Redux store
      dispatch(
        setNewLeadLocal({
          ...newLeadLocal,
          collectionAddress: newLeadLocal.collectionAddress.map(
            (item: any, index: any) =>
              index === activeTab
                ? { ...item, distance: totalDistance ?? 0 }
                : item,
          ),
        }),
      );
      dispatch(setCalculateCollectionDistanceAction(null)); // Clear the distance value after setting it
    }
  }, [distanceCollectionValue, activeTab, formData, dispatch]);

  const handleAddAddress = () => {
    dispatch(addCollectionAddress());
  };

  const handleRemoveAddress = (index: number) => {
    dispatch(removeCollectionAddress(index));
  };

  const handleDeleteAddress = (address: any) => {
    const currentLength = newLeadLocal.collectionAddress.length;
    if (address?.id) {
      dispatch(
        deleteLeadAddressAction({
          leadId: address?.lead_id,
          addressId: address?.id,
        }),
      );
    } else {
      handleRemoveAddress(activeTab);
    }
    // Adjust active tab after deletion
    setTimeout(() => {
      setActiveTab((prevTab) => {
        if (currentLength <= 1) return 0;
        if (prevTab === currentLength - 1) return prevTab - 1; // if last tab deleted
        return prevTab; // stay on same index if not last
      });
    }, 0); // wait for state update
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getCumulativeDistance = (tabIndex: number) => {
    const addresses = newLeadLocal.collectionAddress || [];
    let sum = 0;
    for (let i = 0; i <= tabIndex; i++) {
      sum += Number(addresses[i]?.distance || 0);
    }
    return sum;
  };

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={0.5}
        rowGap={0}
        rowSpacing={0}
        sx={{
          background: "#E5F4F1",
          padding: "10px !important",
          marginTop: "10px !important",
          borderRadius: "5px",
          border: "1px solid #CFCFCF",
          overflow: "hidden",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          style={{
            padding: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: "0px !important",
          }}
        >
          {/* Tabs for Collection Addresses */}
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            aria-label="Collection Address Tabs"
            TabIndicatorProps={{ style: { display: "none" } }} // Hide the tab indicator line
            sx={{
              minHeight: "32px",
              "& .MuiTabs-flexContainer": {
                gap: 1, // adds space between tabs (theme.spacing(1))
              },
            }}
          >
            {newLeadLocal.collectionAddress?.map(
              (address: any, index: number) => (
                <Tab
                  sx={{
                    textAlign: "left",
                    padding: "0px",
                    fontWeight: 600,
                    color: "#333333",
                    marginRight: "10px",
                    backgroundColor:
                      activeTab === index ? "#ffffff" : "transparent",
                    borderRadius: "5px",
                    minHeight: "30px !important",
                    paddingLeft: "6px !important",
                  }}
                  key={index}
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={{ marginRight: 4 }}>{`Collection ${
                        newLeadLocal?.collectionAddress?.length > 1
                          ? index + 1
                          : ""
                      }`}</span>
                      {newLeadLocal?.collectionAddress?.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // prevent tab change
                            handleDeleteAddress(address); // your custom delete function
                          }}
                          disabled={activeTab !== index} // Disable if it's the active tab
                          sx={{
                            cursor:
                              activeTab === index ? "pointer" : "not-allowed",
                          }}
                        >
                          <RemoveCircleRounded
                            sx={{
                              color: activeTab === index ? "#dd4247" : "#ccc",
                            }}
                            fontSize="small"
                          />
                        </IconButton>
                      )}
                    </div>
                  }
                />
              ),
            )}
          </Tabs>
          {newLeadLocal.collectionAddress?.length < 3 &&
            !isBasicDiarySection && (
              <CustomButton
                style={{ padding: "6px 16px", height: "26px" }}
                title="Add"
                onClick={handleAddAddress}
              />
            )}
        </Grid>
        {newLeadLocal.collectionAddress?.map((address: any, index: number) => (
          <Grid
            item
            xs={12}
            key={index}
            role="tabpanel"
            hidden={activeTab !== index} // Hide tabs based on the activeTab value
            id={`tab-panel-${index}`}
            aria-labelledby={`tab-${index}`}
            sx={{
              paddingLeft: "0px !important",
            }}
          >
            {activeTab === index && (
              <Grid container spacing={0.5}>
                {leadCollectionAllFields?.map((field, i) => (
                  <FormField
                    key={i}
                    onPaste={(e: any) =>
                      field?.id === "street" ||
                      field?.id === "town" ||
                      field?.id === "postcode"
                        ? ""
                        : e.preventDefault()
                    }
                    onKeyDown={(event: any) => {
                        if (!field?.patternAllowed) return;

                        if (
                          event.ctrlKey ||
                          event.metaKey ||
                          [
                            "Backspace",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",
                            "ArrowDown",
                            "Delete",
                            "Home",
                            "End",
                            "Enter",
                          ].includes(event.key)
                        ) {
                          return;
                        }

                        const target = event.target as HTMLInputElement;
                        const selectionStart =
                          target.selectionStart ?? target.value.length;
                        const selectionEnd =
                          target.selectionEnd ?? target.value.length;
                        const nextValue =
                          target.value.slice(0, selectionStart) +
                          event.key +
                          target.value.slice(selectionEnd);

                        if (!field.patternAllowed.test(nextValue)) {
                          event.preventDefault();
                        }
                    }}
                    {...{
                      ...field,
                      value:
                        field.id === "distance"
                          ? getCumulativeDistance(activeTab) // cumulative for UI
                          :  isCollectionField(field.id)
                            ? newLeadLocal.collectionAddress?.[activeTab]?.[
                                field.id
                              ] || ""
                            : newLeadLocal?.[field.id] || "",
                      inSingleGridForm: true,

                      options:
                        field.id === "county"
                          ? getCountyOptions(
                              newLeadLocal?.collectionAddress?.[activeTab]
                                ?.country,
                            )
                          : field.id === "type"
                            ? getPropertyTypeOptions(activeTab)
                          : field.options, // Conditional options for county based on country
                      // value: formData
                      //   ? formData[field?.id]
                      //   : "",
                      variant: "outlined",
                      onChange: onChangeAction,
                      onBlur: onBlurAction,
                      isNonMarginTop: true,
                      InputProps: {
                        ...(field?.inputProps && field?.inputProps),
                        ...{ disabled: field?.editReadOnly },
                        sx:
                          field?.type === "text" || field?.type === "password"
                            ? { height: "36px" }
                            : { padding: "10px 4px", height: "36px" },
                      },
                      error: error && error[field?.id],
                      helperText: error && error[field?.id],
                    }}
                  />
                ))}
              </Grid>
            )}
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default CollectionForm;
